import React from 'react';
import { Field } from 'redux-form';
import { Form } from '@syneto/compass-react';
import CreatableReactSelect from 'react-select/creatable';
import { flatAutoCompleteOptions } from '../../helpers/normalize-data';

/*
 * A multi-value select whose values can also be typed in.
 *
 * A sibling of select.js rather than a widening of it: that component keeps its selection in local
 * state resynced by three effects and calls `onChange(event.value)`, which throws on the array a
 * multi-select passes and on the null a clear passes. It also has a lot of untested call sites
 * across the product catalog. Nothing here mirrors state at all - the react-select value is
 * derived from the redux-form value on every render, so there is no second copy to fall out of
 * sync.
 *
 * The field's redux-form value is a plain array of strings, which is the shape the API takes.
 */

export const toSelectOptions = (value, options) => {
	/*
	 * redux-form formats an unset field's value to '', not to []. Without this guard the first
	 * render of an empty field calls .map on a string.
	 */
	const selected = Array.isArray(value) ? value : [];
	const known = flatAutoCompleteOptions(options ?? []);

	return selected.map((entry) => {
		return known.find((option) => {
			return option.value === entry;
		}) ?? { label: entry, value: entry };
	});
};

/*
 * The fallback above is what keeps an existing advisory intact: in edit mode the stored versions
 * can be ones the fleet no longer reports, or ones a capped scan never reached, and an option list
 * that silently drops them would drop them from the next save too.
 */
export const toFieldValue = (selectedOptions) => {
	return (selectedOptions ?? []).map((option) => {
		return option.value;
	});
};

const renderField = (props) => {
	const { options, disabled, input, label, emptyOption, required, meta, onValueChange, isLoading, noOptionsMessage, loadingMessage, formatOptionLabel } = props;
	const { touched, error, warning } = meta;
	const { value, onChange } = input;
	const selectedOptions = toSelectOptions(value, options);

	const handleChange = (selected) => {
		const next = toFieldValue(selected);

		onChange(next);

		if (onValueChange) {
			onValueChange(next);
		}
	};

	/*
	 * Versions are exact, case-preserved strings to the API, so a typed value is never folded into
	 * a listed one that differs only by case - it is a different value and has to stay one.
	 */
	const isValidNewOption = (inputValue) => {
		const candidate = String(inputValue ?? '').trim();

		return Boolean(candidate) && !toFieldValue(selectedOptions).includes(candidate);
	};

	return (
		<Form.Group className="mb-3">
			{ label ? <Form.Label>{ label }{ required ? '*' : null }</Form.Label> : null }

			<CreatableReactSelect
				isMulti
				unstyled
				className={`form-control form-control--multi ${ touched && error ? 'is-invalid' : ''}`}
				classNamePrefix='form-control'
				options={options}
				isDisabled={disabled}
				isLoading={isLoading}
				onChange={handleChange}
				placeholder={emptyOption}
				value={selectedOptions}
				isValidNewOption={isValidNewOption}
				createOptionPosition="first"
				formatCreateLabel={(inputValue) => { return `Add "${inputValue}"`; }}
				formatOptionLabel={formatOptionLabel}
				noOptionsMessage={noOptionsMessage}
				loadingMessage={loadingMessage}
			/>

			{ touched && warning && <span>{ warning }</span> }

			<Form.Control.Feedback type="invalid">
				{ error }
			</Form.Control.Feedback>
		</Form.Group>
	);
};

const CreatableSelect = (props) => { return <Field { ...props } component={ renderField }/>; };

export default CreatableSelect;
